import Fuse, { IFuseOptions } from 'fuse.js'
import { DotNotationPaths } from 'src/utils/types/DotNotationPaths'

/**
 * The threshold for fuzzy matching as a proportion from 0 to 1, where 0 means exact match and 1 means match everything.
 *
 * A lower threshold results in stricter matching, while a higher threshold allows for more lenient matching.
 */
const FUZZY_SEARCH_THRESHOLD = 0.4

/**
 * Hook that returns a function to perform fuzzy search on an array of objects based on a search term.
 */
export const useFuzzySearch = () => {
  /**
   * Performs a fuzzy search on an array of objects based on a search term and specified keys.
   *
   * @param data The array of objects of type `T` to search through.
   * @param keys The keys of the objects of type `T` to search through, specified in dot notation for nested objects.
   * @param searchTerm The search term to use for the fuzzy search.
   * @returns An array of objects of type `T` that match the search term based on the specified keys.
   *
   * @example
   *
   * const data = [
   *   { name: 'Chicken Fingers', category: 'Entree' },
   *   { name: "Cane's Special", category: 'Sauce' },
   * ];
   * const keys = ['name', 'category'];
   * const searchTerm = 'Chiken';
   * const results = fuzzySearch(data, keys, searchTerm); // [{ name: 'Chicken Fingers', category: 'Entree' }]
   */
  const fuzzySearch = <T extends Record<string, any>>(
    data: T[],
    keys: DotNotationPaths<T>[],
    searchTerm: string
  ): T[] => {
    if (!searchTerm) {
      return data
    }

    const options: IFuseOptions<T> = {
      useTokenSearch: true,
      tokenMatch: 'all',
      keys,
      ignoreLocation: true,
      ignoreDiacritics: true,
      threshold: FUZZY_SEARCH_THRESHOLD,
    }

    const fuse = new Fuse(data, options)
    const results = fuse.search(searchTerm)

    return results.map(result => result.item)
  }

  return {
    fuzzySearch,
  }
}
